import { getRouteAccessForPath } from "~/code/page-routes";
import type { SidebarItemTreeDto } from "@/generated/models";

export const normalizePermissionToken = (permission: string | null | undefined) => {
    const normalized = String(permission || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-");
    return normalized === "migrations" ? "migration" : normalized;
};

export const expandPermissionCandidates = (permission: string | null | undefined) => {
    const normalized = normalizePermissionToken(permission);

    if (!normalized) {
        return [] as string[];
    }

    const candidates = new Set<string>([normalized]);

    if (normalized.endsWith("-server")) {
        candidates.add(normalized.slice(0, -"-server".length));
    } else {
        candidates.add(`${normalized}-server`);
    }

    return Array.from(candidates);
};

export const uniquePermissions = (permissions: Array<string | null | undefined>) => {
    return permissions.filter(
        (permission, index, list): permission is string =>
            Boolean(permission) && list.indexOf(permission) === index
    );
};

export const normalizeRoutePath = (path: string | null | undefined) => {
    const normalized = String(path || "")
        .split("?")[0]
        .split("#")[0]
        .trim()
        .replace(/\/+$/, "")
        .toLowerCase();

    if (!normalized || normalized === "/") {
        return "/";
    }

    return normalized.startsWith("/") ? normalized : `/${normalized}`;
};

export const findSidebarItemByLink = (
    items: SidebarItemTreeDto[],
    targetPath: string
): SidebarItemTreeDto | null => {
    for (const item of items || []) {
        if (normalizeRoutePath(item?.link) === targetPath) {
            return item;
        }

        const nestedMatch = findSidebarItemByLink(item?.children || [], targetPath);

        if (nestedMatch) {
            return nestedMatch;
        }
    }

    return null;
};

export const extractPermissionPrefix = (permissionEntry: string | null | undefined) => {
    const rawPrefix = String(permissionEntry || "").split(":")[0];
    return normalizePermissionToken(rawPrefix);
};

export const matchesSidebarTitle = (permissionPrefix: string, titleToken: string) => {
    if (!permissionPrefix || !titleToken) {
        return false;
    }

    return (
        permissionPrefix === titleToken ||
        permissionPrefix.endsWith(`-${titleToken}`)
    );
};

export const getFallbackPermissionPrefixForPath = (path: string | null | undefined) => {
    const normalizedPath = normalizeRoutePath(path);
    const routeParts = normalizedPath.split("/").filter(Boolean);
    const leaf = routeParts.pop();

    if (!leaf || leaf === "auth") {
        return "edit-content";
    }

    return `edit-${normalizePermissionToken(leaf)}`;
};

export const resolveRoutePermissionPrefixes = ({
    routePath,
    sidebarTree,
    userPermissionEntries,
    configuredPermissionPrefixes,
}: {
    routePath: string;
    sidebarTree: SidebarItemTreeDto[];
    userPermissionEntries: string[];
    configuredPermissionPrefixes: string[];
}) => {
    const normalizedPath = normalizeRoutePath(routePath);
    const routePermissions = getRouteAccessForPath(normalizedPath)?.permissions || [];
    if (routePermissions.length) {
        return uniquePermissions(routePermissions.flatMap(expandPermissionCandidates));
    }
    const sidebarItem = findSidebarItemByLink(sidebarTree || [], normalizedPath);
    const titleToken = normalizePermissionToken(sidebarItem?.title);

    const userPermissionPrefixes = uniquePermissions(
        (userPermissionEntries || [])
            .map((permissionEntry) => extractPermissionPrefix(permissionEntry))
            .filter((permissionPrefix) => matchesSidebarTitle(permissionPrefix, titleToken))
    );

    if (userPermissionPrefixes.length > 0) {
        return uniquePermissions(
            userPermissionPrefixes.flatMap((permission) =>
                expandPermissionCandidates(permission)
            )
        );
    }

    const availablePermissionPrefixes = uniquePermissions(
        (configuredPermissionPrefixes || [])
            .map((permissionPrefix) => normalizePermissionToken(permissionPrefix))
            .filter((permissionPrefix) => matchesSidebarTitle(permissionPrefix, titleToken))
    );

    if (availablePermissionPrefixes.length > 0) {
        return uniquePermissions(
            availablePermissionPrefixes.flatMap((permission) =>
                expandPermissionCandidates(permission)
            )
        );
    }

    return expandPermissionCandidates(getFallbackPermissionPrefixForPath(routePath));
};